/**
 * ZoneDrawingMapOptimized Component
 *
 * Optimized version of ZoneDrawingMap with dealer-level bubbles for performance
 * Shows dealer locations with recall counts instead of individual recall markers
 */

import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import type * as GeoJSON from 'geojson';
import { pointOnFeature } from '@turf/point-on-feature';

// Restore original dealer location types
import type { DealerLocation } from '../hooks/useViewportRecallsForZoneMap';
import type { Zone } from '@vehix/portal/services/api/tenants';
import { cn } from '@vehix/portal/lib/utils';
import {
  getZoneColor,
  getZoneSizeLabel,
  getZoneBorderColor,
  getZoneDashPattern
} from '../utils/zoneStatistics';
import {
  getZoneSizeTier,
  getZoneSizeTierShortLabel,
  isZoneTooLarge,
  type ZoneSizeAudience
} from '../utils/zoneSizeThresholds';
import { ASSIGNED_ZONE_COLOR, AVAILABLE_ZONE_COLOR, VEHIX_ZONE_PALETTE } from '../utils/oemColors';
import { ZoneLabelOverlay } from './ZoneLabelOverlay';

/**
 * Presentation-mode configuration for the zone map. When this prop is
 * provided to the component, the map switches into a customer-shareable
 * surface: zones are colored by OEM brand, dealer bubbles are forced off
 * (unless `showRecallDensity` is true), and a centroid card is rendered
 * for each selected zone with size + recall KPIs. Drawing tools are also
 * disabled in this mode (see `drawingEnabled`).
 */
export interface PresentationModeConfig {
  /** Render a centroid Marker with zone name + size + KPIs over each selected zone. */
  showCentroidCards: boolean;
  /** Toggle visibility of the "Current: N recalls" line in the zone label. */
  showNowRecalls: boolean;
  /** Toggle visibility of the "At creation: N recalls" line in the zone label. */
  showCreationRecalls: boolean;
  /**
   * zone_id → name of the tenant the zone is assigned to. A zone missing from
   * the map is available. Assigned zones render in muted, dashed slate so the
   * zones still open for sale carry the brand color. Omit while loading —
   * every zone then renders as available.
   */
  tenantByZone?: Record<string, string>;
  /**
   * Show the tenant's name on assigned zones' labels. When false the label
   * says only "Assigned" — availability coloring is unaffected. Off when the
   * map is about to be shown to a dealer who shouldn't see competitors' names.
   */
  showTenantNames: boolean;
  /**
   * Toggle the recall-density visualization (smaller, OEM-colored dealer
   * bubbles). When false, the map shows only zones + centroid cards for a
   * clean shareable artifact. When true, dealer bubbles overlay the zones
   * to show recall pressure regions.
   */
  showRecallDensity: boolean;
  /** zone_id → current recall count (from useCurrentRecallCounts). */
  recallCountsNow?: Record<string, number>;
  /**
   * Zone IDs in the user's active selection. Selected zones render at full
   * opacity with a centroid card; non-selected zones render as faded outlines
   * for context. An empty array means "treat all zones in viewport as selected".
   */
  selectedZoneIds: string[];
  /**
   * Hex color pair used to paint dealer bubbles when the recall-density
   * layer is on. Brand-uniform across OEMs (Vehix bronze) so the dots
   * contrast cleanly against the Vehix-orange zone strokes.
   */
  bubbleColor?: { fill: string; stroke: string };
  /**
   * zone_id → FeatureCollection of per-ZIP boundary polygons for that zone.
   * Used to render thin gray subdivision lines inside each zone polygon so
   * customers can see the ZIP-level shape of their territory. Omit while
   * boundaries are still being fetched; the component renders zone outline
   * + halo only until the data arrives.
   */
  zipBoundariesByZone?: Map<
    string,
    GeoJSON.FeatureCollection<GeoJSON.Polygon | GeoJSON.MultiPolygon>
  >;
  /**
   * zone_id → palette index into `VEHIX_ZONE_PALETTE`. Produced by the
   * adjacency-aware four-color graph-coloring pass in
   * `computeZoneColorAssignment`. Adjacent zones receive different indexes
   * so two touching zones never share a fill — the boundary becomes a
   * contrast edge between two color fields rather than a stroke trying to
   * separate same-color regions.
   */
  zoneColorAssignment?: Map<string, number>;
}

// Helper function to get short size label. Buckets are per-OEM, so the zone's
// OEM has to reach here — it travels via the GeoJSON feature properties, as does
// the audience (Presentation mode caps the ladder at 'L').
const getShortSizeLabel = (
  recallCount: number,
  oem: string | undefined,
  audience: ZoneSizeAudience = 'internal'
): string => getZoneSizeTierShortLabel(getZoneSizeTier(recallCount, oem), audience);

const getSizeLabelColor = (
  recallCount: number,
  oem: string | undefined,
  audience: ZoneSizeAudience = 'internal'
): string =>
  // Use CSS variables that match the application theme. Only the top tier is
  // called out; everything below it reads as normal. A customer-facing chip reads
  // "L", so the destructive red would be describing a tier the label doesn't show.
  audience !== 'customer' && isZoneTooLarge(recallCount, oem)
    ? 'hsl(var(--destructive))'
    : 'hsl(var(--primary))';

export interface ZoneDrawingMapOptimizedProps {
  dealerLocations: DealerLocation[];
  zones: Zone[];
  onShapeDrawn: (geometry: GeoJSON.Geometry) => void;
  onMapBoundsChange?: (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => void;
  selectedZoneId?: string | null;
  loading?: boolean;
  onZoneDelete?: (zoneId: string) => void;
  onRemoveZipCode?: (zone: Zone, zipCode: string) => void;
  clearDrawnShapes?: boolean;
  showBubbles?: boolean;
  uniformColor?: boolean;
  /**
   * When `false`, Leaflet.Draw controls are not added to the map. Defaults to
   * `true` for backward compatibility with the Design-mode call site.
   */
  drawingEnabled?: boolean;
  /**
   * zone_id → current recall count (from useCurrentRecallCounts). Applies in
   * both modes. Zone polygons prefer this over the denormalized
   * `total_recalls_current` column, which the nightly refresh job leaves at 0
   * for any zone created since its last run — a zone drawn today would
   * otherwise render as "Small (0)" until the following morning.
   */
  recallCountsNow?: Record<string, number>;
  /**
   * zone_id → the crawl window behind that zone's count: `latest` is the date the
   * count is "as of", `oldest` the oldest crawl still contributing. A zone whose ZIPs
   * are covered by several crawl footprints can be partly built on older data, and
   * without this the map gives no way to tell that apart from a freshly crawled zone.
   *
   * Design mode only — these dates are internal plumbing, and Presentation mode output
   * is handed to dealerships.
   */
  crawlDatesNow?: Record<string, { latest?: string; oldest?: string }>;
  /**
   * When provided, switches the map into Presentation mode (OEM-branded zone
   * fills, faded non-selected zones, centroid cards over selected zones).
   * When omitted/undefined, the map renders in Design mode (today's behavior).
   */
  presentationMode?: PresentationModeConfig;
}

// Fix for default markers in React-Leaflet
delete (L.Icon.Default.prototype as unknown as { _getIconUrl: unknown })['_getIconUrl'];
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

// Create custom icon for dealer bubbles (Design mode — bronze, sized 25–45,
// recall count visible inside).
const createDealerIcon = (recallCount: number) => {
  let size;
  if (recallCount === 1) {
    size = 25;
  } else if (recallCount <= 5) {
    size = 30;
  } else if (recallCount <= 10) {
    size = 35;
  } else if (recallCount <= 20) {
    size = 40;
  } else {
    size = 45;
  }

  const baseColor = '#7C5F2C'; // Bronze from Vehix (restored original)
  let opacity;
  if (recallCount === 1) {
    opacity = 0.7;
  } else if (recallCount <= 5) {
    opacity = 0.8;
  } else if (recallCount <= 15) {
    opacity = 0.9;
  } else {
    opacity = 1.0;
  }

  const backgroundColor = recallCount > 15 ? '#5C4521' : baseColor;
  const textColor = '#FFFFFF';

  const html = `<div style="background-color: ${backgroundColor}; color: ${textColor};
    border-radius: 50%;
    width: ${size}px; height: ${size}px;
    display: flex; align-items: center; justify-content: center;
    font-weight: bold; border: 1px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    font-size: 12px; font-family: Arial, sans-serif; opacity: ${opacity};
    transition: all 0.2s ease;">
    ${recallCount}
    </div>`;

  return L.divIcon({
    html: html,
    className: 'custom-marker-icon',
    iconSize: L.point(size, size, true)
  });
};

/**
 * Presentation-mode dealer icon. Smaller (12–28px), OEM-colored, no recall
 * count rendered inside. Designed to read as a unified "field of dots"
 * showing recall pressure rather than as individually-clickable markers.
 */
const createPresentationDealerIcon = (
  recallCount: number,
  fill: string,
  stroke: string
): L.DivIcon => {
  let size: number;
  if (recallCount === 1) size = 12;
  else if (recallCount <= 5) size = 16;
  else if (recallCount <= 10) size = 20;
  else if (recallCount <= 20) size = 24;
  else size = 28;

  const html = `<div style="
    background-color: ${fill};
    border: 1.5px solid ${stroke};
    border-radius: 50%;
    width: ${size}px;
    height: ${size}px;
    box-shadow: 0 1px 3px rgba(15,23,42,0.25);
    opacity: 0.85;
  "></div>`;

  return L.divIcon({
    html,
    className: 'custom-marker-icon presentation-marker',
    iconSize: L.point(size, size, true)
  });
};

// Memoized component to handle map initialization and drawing controls

const MapController = React.memo<{
  onShapeDrawn: (geometry: GeoJSON.Geometry) => void;
  dealerLocations: DealerLocation[];
  zones: Zone[];
  selectedZoneId?: string | null;
  onMapBoundsChange?: (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => void;
  onZoneDelete?: (zoneId: string) => void;
  onRemoveZipCode?: (zone: Zone, zipCode: string) => void;
  clearDrawnShapes?: boolean;
  showBubbles?: boolean;
  uniformColor?: boolean;
  drawingEnabled?: boolean;
  recallCountsNow?: Record<string, number>;
  crawlDatesNow?: Record<string, { latest?: string; oldest?: string }>;
  presentationMode?: PresentationModeConfig;
}>(
  ({
    onShapeDrawn,
    dealerLocations,
    zones,
    selectedZoneId,
    onMapBoundsChange,
    onZoneDelete,
    onRemoveZipCode,
    clearDrawnShapes,
    showBubbles = true,
    uniformColor = false,
    drawingEnabled = true,
    recallCountsNow,
    crawlDatesNow,
    presentationMode
  }) => {
    const map = useMap();
    const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
    const editableZonesRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
    const drawControlRef = useRef<L.Control.Draw | null>(null);
    const markerClusterGroupRef = useRef<any>(null);
    const zoneLayersRef = useRef<Map<string, L.GeoJSON>>(new Map());
    // Holds the per-zone DivIcon Markers rendered as "centroid cards" in
    // Presentation mode. Lifecycle is independent of the polygon layers so we
    // can re-render cards when the user toggles the "Now"/"Creation" KPI lines
    // without re-creating the polygons underneath.
    const centroidMarkersRef = useRef<L.Marker[]>([]);
    // Presentation-mode-only auxiliary layer for per-ZIP subdivision lines
    // so customers can see the ZIP-level shape of their territory. Zone-to-
    // zone separation is handled by the heavy stroke on the zone polygon
    // itself, not a halo (z-ordering against fills makes halos unreliable).
    const zoneZipSubdivisionLayersRef = useRef<L.GeoJSON[]>([]);
    // Presentation-mode-only raw (un-clustered) dealer markers. Showing each
    // dealer as an individual dot lets the customer see actual recall density
    // rather than aggregated cluster numbers — a "field of points" visual
    // that works the way Mapbox/Carto density layers do.
    const presentationDealerMarkersRef = useRef<L.Marker[]>([]);
    const boundsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Dynamically load leaflet-draw CSS to avoid Vite build issues
    useEffect(() => {
      if (typeof document !== 'undefined') {
        // Check if already loaded
        const existingLink = document.querySelector('link[data-leaflet-draw-css]');
        if (!existingLink) {
          // Use CDN fallback for leaflet-draw CSS
          try {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.setAttribute('data-leaflet-draw-css', 'true');
            link.href = 'https://unpkg.com/leaflet-draw@1.0.4/dist/leaflet.draw.css';
            document.head.appendChild(link);
          } catch (error) {}
        }
      }
    }, []);

    // Fix map size calculation - ensures shapes render correctly on all sides
    useEffect(() => {
      // Invalidate size when map is ready to fix rendering issues
      const timer = setTimeout(() => {
        if (map && map.getContainer()) {
          try {
            map.invalidateSize();
          } catch (error) {
            // Silently handle errors
          }
        }
      }, 100);

      // Second invalidation for tab-switch scenarios where container
      // dimensions may not be final at 100ms (Radix TabsContent mount)
      const timer2 = setTimeout(() => {
        if (map && map.getContainer()) {
          try {
            map.invalidateSize();
          } catch (error) {
            // Silently handle errors
          }
        }
      }, 300);

      // Also invalidate on window resize
      const handleResize = () => {
        if (map && map.getContainer()) {
          try {
            map.invalidateSize();
          } catch (error) {
            // Silently handle errors
          }
        }
      };

      window.addEventListener('resize', handleResize);

      return () => {
        clearTimeout(timer);
        clearTimeout(timer2);
        window.removeEventListener('resize', handleResize);
      };
    }, [map]);

    // Initialize drawing controls and add custom popup styles
    useEffect(() => {
      // Add custom CSS for modern popup styling and enhanced zone boundaries
      const styleId = 'modern-zone-popup-styles';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
        /* Clean zone boundary styles */
        .zone-boundary {
          transition: opacity 0.2s ease;
        }
        
        .zone-boundary:hover {
          opacity: 1 !important;
        }
        
        /* Modern popup styles */
        .modern-zone-popup .leaflet-popup-content-wrapper {
          padding: 0;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          background: transparent;
        }
        .modern-zone-popup .leaflet-popup-content {
          margin: 0;
          line-height: 1.4;
        }
        .modern-zone-popup .leaflet-popup-tip {
          background: hsl(var(--background));
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        .modern-zone-popup .leaflet-popup-close-button {
          background: hsl(var(--muted));
          border-radius: 50%;
          width: 24px;
          height: 24px;
          text-align: center;
          line-height: 22px;
          right: 8px;
          top: 8px;
          color: hsl(var(--muted-foreground));
          font-size: 14px;
          font-weight: bold;
          border: 1px solid hsl(var(--border));
          transition: all 0.15s ease;
        }
        .modern-zone-popup .leaflet-popup-close-button:hover {
          background: hsl(var(--destructive));
          color: hsl(var(--destructive-foreground));
          border-color: hsl(var(--destructive));
        }
      `;
        document.head.appendChild(style);
      }

      drawnItemsRef.current.addTo(map);
      editableZonesRef.current.addTo(map);

      // Skip wiring the Leaflet.Draw control entirely when the page is in
      // Presentation mode — drawing/editing makes no sense on a customer-
      // shareable surface, and removing the control reclaims map chrome.
      // The two FeatureGroups above stay attached so zone polygons still render.
      if (!drawingEnabled) {
        return () => {
          map.removeLayer(drawnItemsRef.current);
          map.removeLayer(editableZonesRef.current);
        };
      }

      drawControlRef.current = new L.Control.Draw({
        draw: {
          polygon: {
            allowIntersection: false,
            showArea: false,
            shapeOptions: {
              color: '#d4854a', // Primary brand orange - high contrast in both light and dark modes
              weight: 3,
              opacity: 1.0, // Full opacity for better visibility
              fillColor: '#d4854a',
              fillOpacity: 0.2
            }
          },
          polyline: {
            shapeOptions: {
              color: '#d4854a', // Primary brand orange - high contrast in both light and dark modes
              weight: 3,
              opacity: 1.0 // Full opacity for better visibility
            }
          },
          rectangle: {
            showArea: false,
            shapeOptions: {
              color: '#d4854a', // Primary brand orange - high contrast in both light and dark modes
              weight: 3,
              opacity: 1.0, // Full opacity for better visibility
              fillColor: '#d4854a',
              fillOpacity: 0.2
            }
          },
          circle: false, // Disabled - not fully tested
          marker: false,
          circlemarker: false
        },
        edit: false as any // Disabled - edit and delete buttons not fully tested
      });

      map.addControl(drawControlRef.current);

      const handleDrawCreated = (e: any) => {
        const layer = e.layer;
        drawnItemsRef.current.clearLayers();
        drawnItemsRef.current.addLayer(layer);
        const geojson = layer.toGeoJSON().geometry;

        onShapeDrawn(geojson);
      };

      const handleDrawDeleted = (e: any) => {
        // Handle deletion of zones through the map delete tool
        if (onZoneDelete) {
          e.layers.eachLayer((layer: L.Layer) => {
            // Check if this layer has a zoneId property (set when we add zone polygons)
            const options = (layer as L.Marker | L.Polygon).options as L.PathOptions & {
              zoneId?: string;
            };
            if (options && options.zoneId) {
              onZoneDelete(options.zoneId);
            }
          });
        }
      };

      map.on(L.Draw.Event.CREATED, handleDrawCreated);
      map.on(L.Draw.Event.DELETED, handleDrawDeleted);

      return () => {
        map.off(L.Draw.Event.CREATED, handleDrawCreated);
        map.off(L.Draw.Event.DELETED, handleDrawDeleted);
        if (drawControlRef.current) {
          map.removeControl(drawControlRef.current);
        }
        map.removeLayer(drawnItemsRef.current);
        map.removeLayer(editableZonesRef.current);
      };
    }, [map, onShapeDrawn, onZoneDelete, drawingEnabled]);

    // Clear drawn shapes when clearDrawnShapes prop changes
    useEffect(() => {
      if (clearDrawnShapes) {
        drawnItemsRef.current.clearLayers();
      }
    }, [clearDrawnShapes]);

    // Add dealer markers with stable server-side data + client-side clustering.
    // Two visual modes share this effect: Design mode uses bronze bubbles
    // with the recall count rendered inside; Presentation mode (when
    // `presentationMode.showRecallDensity` is true) uses smaller, OEM-
    // colored, numberless bubbles that read as a unified density layer.
    useEffect(() => {
      // Remove existing cluster group on every effect run so toggling modes
      // or flipping density on/off doesn't leave stale markers behind.
      // Wipe both possible bubble layers on every effect run so toggling
      // between Design (cluster) and Presentation (raw markers) doesn't
      // leave stale layers behind.
      if (markerClusterGroupRef.current) {
        map.removeLayer(markerClusterGroupRef.current);
        markerClusterGroupRef.current = null;
      }
      presentationDealerMarkersRef.current.forEach(marker => map.removeLayer(marker));
      presentationDealerMarkersRef.current = [];

      // Bubbles are visible when:
      //  - Design mode is active and the user has the bubble switch on, OR
      //  - Presentation mode is active and showRecallDensity is on.
      const isPresentation = !!presentationMode;
      const presentationDensityOn = !!presentationMode?.showRecallDensity;
      const designBubblesOn = !isPresentation && showBubbles;
      const shouldRenderBubbles = designBubblesOn || presentationDensityOn;
      if (!shouldRenderBubbles) {
        return;
      }

      const presentationFill = presentationMode?.bubbleColor?.fill ?? '#7C5F2C';
      const presentationStroke = presentationMode?.bubbleColor?.stroke ?? '#5C4521';

      // Presentation mode: render every dealer as an individual marker
      // directly on the map (no clustering). This is the "raw density"
      // visualization the user asked for — a field of dots that lets the
      // customer see actual recall pressure point-by-point rather than
      // aggregated counts inside a cluster icon.
      if (presentationDensityOn) {
        dealerLocations.forEach(dealer => {
          const icon = createPresentationDealerIcon(
            dealer.recallCount,
            presentationFill,
            presentationStroke
          );
          const marker = L.marker([dealer.coordinates.latitude, dealer.coordinates.longitude], {
            icon,
            interactive: false,
            keyboard: false
          });
          marker.addTo(map);
          presentationDealerMarkersRef.current.push(marker);
        });

        return () => {
          presentationDealerMarkersRef.current.forEach(marker => map.removeLayer(marker));
          presentationDealerMarkersRef.current = [];
        };
      }

      // Design mode: keep the existing clustered behavior — the drawing
      // workflow benefits from aggregation when zoomed out.
      markerClusterGroupRef.current = (L as any).markerClusterGroup({
        maxClusterRadius: 60,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
        iconCreateFunction: (cluster: any) => {
          let totalRecalls = 0;
          cluster.getAllChildMarkers().forEach((marker: any) => {
            if (marker.options.recallCount) {
              totalRecalls += marker.options.recallCount;
            }
          });

          return new L.DivIcon({
            html: `<div style="background-color: #7C5F2C; color: white;
            border-radius: 50%; width: 40px; height: 40px;
            display: flex; align-items: center; justify-content: center;
            font-weight: bold; border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            font-size: 14px; opacity: 0.9;">
            ${totalRecalls}
            </div>`,
            className: 'custom-cluster-icon',
            iconSize: new L.Point(40, 40)
          });
        }
      });

      const markers = dealerLocations.map(dealer => {
        const icon = createDealerIcon(dealer.recallCount);

        const marker = L.marker([dealer.coordinates.latitude, dealer.coordinates.longitude], {
          icon,
          recallCount: dealer.recallCount // Store for cluster calculations
        } as any);

        marker.bindPopup(`
        <div style="padding: 12px; max-width: 300px;">
          <h3 style="font-weight: 600; font-size: 16px; margin-bottom: 8px;">${dealer.dealerName}</h3>
          ${
            dealer.address && dealer.address !== 'N/A'
              ? `<p style="font-size: 14px; color: #666; margin-bottom: 12px;">${dealer.address}</p>`
              : ''
          }
          <div style="display: flex; gap: 8px; flex-wrap: wrap;">
            <span style="padding: 4px 8px; background: #fee2e2; color: #991b1b; border-radius: 4px; font-size: 12px; font-weight: 500;">
              ${dealer.recallCount} recalls
            </span>
            <span style="padding: 4px 8px; background: #dbeafe; color: #1e40af; border-radius: 4px; font-size: 12px; font-weight: 500;">
              ${dealer.vehicleCount} vehicles
            </span>
          </div>
        </div>
      `);

        return marker;
      });

      markerClusterGroupRef.current.addLayers(markers);
      map.addLayer(markerClusterGroupRef.current);

      return () => {
        if (markerClusterGroupRef.current) {
          map.removeLayer(markerClusterGroupRef.current);
        }
      };
    }, [map, dealerLocations, showBubbles, presentationMode]);

    // Optimized zone rendering with memoization. In Presentation mode the
    // styling switches to OEM-branded colors (one fill/stroke per OEM) and
    // selection-aware opacity (selected = vivid, non-selected = faded for
    // context). In Design mode the existing recall-tier coloring is preserved.
    const zoneGeoJsonData = useMemo(() => {
      const presentationSelected = presentationMode?.selectedZoneIds ?? [];
      const presentationActive = !!presentationMode;
      // Empty selection in Presentation mode means "show all visible zones as
      // selected" — gives the user a sane default before they pick zones.
      const allSelectedInPresentation = presentationActive && presentationSelected.length === 0;

      return zones
        .filter(zone => zone.metadata && zone.metadata['geometry'])
        .map((zone, index) => {
          const geometry = zone.metadata!['geometry'];
          // Current count, not the creation baseline — polygon colour, dash pattern, the
          // S/M/L/XL chip and the "Zone Too Large" marker all derive from this, so a zone
          // whose volume has moved since it was drawn was being coloured on stale data.
          // The live RPC figure wins over the denormalized column, which is still 0 for
          // any zone created since the nightly refresh last ran.
          const recallCount = recallCountsNow?.[zone.id] ?? zone.total_recalls_current ?? 0;
          const zipCodeCount = zone.zip_code_count || 0;
          const zipCodes = zone.zip_codes || [];
          const isLegacySelected = selectedZoneId === zone.id;
          const isInPresentationSelection =
            presentationActive &&
            (allSelectedInPresentation || presentationSelected.includes(zone.id));

          const isAssigned = presentationActive && !!presentationMode?.tenantByZone?.[zone.id];

          let color: string;
          let borderColor: string;
          if (isAssigned) {
            // Assigned zones step back to neutral slate — figure/ground: the
            // zones still available for sale keep the brand palette and read
            // first. The dash in the style callback below carries the same
            // signal without relying on color.
            color = ASSIGNED_ZONE_COLOR.fill;
            borderColor = ASSIGNED_ZONE_COLOR.stroke;
          } else if (presentationActive) {
            // Adjacency-aware four-color fill — adjacent zones GUARANTEED
            // get different palette indexes (see computeZoneColorAssignment).
            // All four shades are warm-brown family so the map still reads
            // as a Vehix-branded artifact. Stroke is a neutral slate that
            // tightens the territory edge without competing with the fill
            // (the fill carries adjacency contrast now).
            const paletteIndex = presentationMode?.zoneColorAssignment?.get(zone.id) ?? 1;
            const paletteEntry =
              VEHIX_ZONE_PALETTE[paletteIndex % VEHIX_ZONE_PALETTE.length] ??
              VEHIX_ZONE_PALETTE[1]!;
            color = paletteEntry.fill;
            borderColor = '#475569';
          } else if (uniformColor) {
            color = '#d4854a';
            borderColor = '#b5693a';
          } else {
            color = getZoneColor(recallCount, zone.oem);
            borderColor = getZoneBorderColor(isLegacySelected, recallCount, index, zone.oem);
          }

          return {
            id: zone.id,
            feature: {
              type: 'Feature',
              geometry: geometry as GeoJSON.Geometry,
              properties: {
                zoneId: zone.id,
                zoneName: zone.zone_name,
                recallCount,
                zipCodeCount,
                zipCodes,
                color,
                isSelected: isLegacySelected,
                isInPresentationSelection,
                presentationActive,
                isAssigned,
                zoneIndex: index,
                borderColor,
                // Size buckets are per-OEM. The style/popup callbacks below only
                // see feature.properties, not the zone, so the OEM rides along.
                oem: zone.oem,
                // Same reason: the popup callback has no access to the zone or to
                // crawlDatesNow, so the crawl window rides along too.
                crawlLatest: crawlDatesNow?.[zone.id]?.latest ?? zone.latest_snapshot_date,
                crawlOldest: crawlDatesNow?.[zone.id]?.oldest ?? zone.oldest_snapshot_date
              }
            } as GeoJSON.Feature
          };
        });
    }, [zones, selectedZoneId, uniformColor, recallCountsNow, crawlDatesNow, presentationMode]);

    // Render zones
    useEffect(() => {
      // Clear existing zones from both editable group and direct map
      editableZonesRef.current.clearLayers();
      zoneLayersRef.current.forEach(layer => map.removeLayer(layer));
      zoneLayersRef.current.clear();

      // Add zones as polygons to the editable feature group
      zoneGeoJsonData.forEach(({ id, feature }) => {
        const geoJsonLayer = L.geoJSON(feature, {
          // Add zoneId to layer options for deletion tracking
          zoneId: feature.properties!['zoneId'],
          // Presentation zones have no popup, so no pointer cursor either.
          interactive: !feature.properties!['presentationActive'],
          style: () => {
            const props = feature.properties!;
            const isSelected = props['isSelected'];
            const recallCount = props['recallCount'] || 0;
            const zoneIndex = props['zoneIndex'] || 0;
            const presentationActive = props['presentationActive'];
            const isInPresentationSelection = props['isInPresentationSelection'];
            const isAssigned = props['isAssigned'];

            // Presentation mode: fill carries the adjacency contrast (each
            // zone gets a different shade from VEHIX_ZONE_PALETTE so two
            // touching zones never share a color). Stroke retreats to a
            // neutral slate at moderate weight — it just defines the
            // territory edge crisply rather than trying to be the boundary
            // signal. Selected zones at fillOpacity 0.55 are vivid; faded
            // context zones at 0.15 stay readable without dominating.
            // Assigned zones sit a step lower on both and add a dashed edge.
            if (presentationActive) {
              const fillOpacity = isAssigned
                ? isInPresentationSelection
                  ? 0.3
                  : 0.12
                : isInPresentationSelection
                  ? 0.55
                  : 0.15;
              return {
                fillColor: props['color'],
                fillOpacity,
                color: props['borderColor'],
                weight: isInPresentationSelection ? 1.5 : 0.75,
                opacity: isInPresentationSelection ? 0.9 : 0.5,
                ...(isAssigned && { dashArray: '4 3' }),
                className: 'zone-boundary',
                lineCap: 'round',
                lineJoin: 'round'
              };
            }

            // Design mode: existing recall-tier styling preserved unchanged.
            return {
              // Fill styling
              fillColor: props['color'],
              fillOpacity: isSelected ? 0.4 : 0.2, // Clean, consistent opacity

              // Clean border styling - matches zone color for unified look
              color: props['borderColor'], // Matches zone color or black when selected
              weight: isSelected ? 4 : 3, // Clean, consistent border weight
              opacity: isSelected ? 1 : 0.8, // Full opacity for selected, slightly reduced for others

              // Restore dash patterns for better zone distinction, especially for extra large zones
              dashArray: getZoneDashPattern(recallCount, zoneIndex, props['oem']),

              // Clean visual effects
              className: 'zone-boundary',
              lineCap: 'round',
              lineJoin: 'round'
            };
          },
          onEachFeature: (feature: any, layer: any) => {
            const props = feature.properties;
            const zoneId = props!['zoneId'];
            const recallCount = props!['recallCount'] || 0;
            const zoneOem = props!['oem'];
            const isExtraLarge = isZoneTooLarge(recallCount, zoneOem);

            // Store zoneId on the layer for deletion handling
            (layer as any).options = { ...((layer as any).options || {}), zoneId };

            // Presentation mode has no popup: the zone labels already carry name, size,
            // ZIP count and availability, and the popup's ZIP-removal / delete controls
            // are editing tools. Everything below is the Design-mode popup.
            if (props!['presentationActive']) return;
            const audience: ZoneSizeAudience = 'internal';
            const showOversizedAdvisory = isExtraLarge;

            const zipCodes = props!['zipCodes'] || [];
            const sizeLabel = getShortSizeLabel(recallCount, zoneOem, audience);
            const sizeLabelColor = getSizeLabelColor(recallCount, zoneOem, audience);
            const fullSizeLabel = getZoneSizeLabel(recallCount, zoneOem, audience);

            // Crawl window behind the count.
            //
            // "Counted as of" is the newest crawl for this zone's tenant, not the last
            // time anything was crawled — it can sit weeks back, and it can move earlier
            // when a zone is reassigned. When an older crawl is still contributing, the
            // cutoff left at least one of the zone's feeds behind, so part of the
            // territory is counted from older data or not at all. That case gets a marker
            // and the gap in days; the clean case just states the date.
            const crawlLatest = props!['crawlLatest'];
            const crawlOldest = props!['crawlOldest'];
            const hasEvictedFeed = !!crawlOldest && crawlOldest !== crawlLatest;
            const crawlGapDays = hasEvictedFeed
              ? Math.round(
                  (new Date(crawlLatest).getTime() - new Date(crawlOldest).getTime()) / 86_400_000
                )
              : 0;
            const crawlWindow = !crawlLatest
              ? ''
              : `<div style="
                    margin-top: 10px;
                    font-size: 10px;
                    color: hsl(var(--muted-foreground));
                  ">Counted as of ${crawlLatest}${
                    hasEvictedFeed
                      ? ` · ⚠ oldest crawl still counted ${crawlOldest}, ${crawlGapDays} days earlier`
                      : ''
                  }</div>`;

            // Create modern ZIP codes display with remove buttons
            const zipCodesDisplay =
              zipCodes.length > 0
                ? `<div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid hsl(var(--border));">
                <div style="
                  margin-bottom: 12px;
                ">
                  <span style="
                    font-size: 12px; 
                    font-weight: 600; 
                    color: hsl(var(--foreground));
                    display: block;
                    margin-bottom: 4px;
                  ">ZIP Codes (${zipCodes.length})</span>
                </div>
                <div style="
                  max-height: 120px; 
                  overflow-y: auto; 
                  display: flex; 
                  flex-wrap: wrap; 
                  gap: 6px;
                  padding-top: 8px;
                ">
                  ${zipCodes
                    .map((zip: string) => {
                      const canRemove = onRemoveZipCode && zipCodes.length > 1;
                      return `<span style="
                      background: hsl(var(--muted)); 
                      border: 1px solid hsl(var(--border));
                      padding: 4px 8px; 
                      border-radius: 6px; 
                      font-family: ui-monospace, 'SF Mono', 'Monaco', 'Cascadia Code', 'Roboto Mono', monospace;
                      font-size: 11px;
                      font-weight: 500;
                      color: hsl(var(--foreground));
                      display: inline-block;
                      position: relative;
                      ${canRemove ? 'padding-right: 24px; margin-right: 2px;' : ''}
                      transition: all 0.15s ease;
                    " ${canRemove ? "onmouseover=\"this.style.background='hsl(var(--muted) / 0.8)'; this.style.borderColor='hsl(var(--border))'\"" : ''} 
                       ${canRemove ? "onmouseout=\"this.style.background='hsl(var(--muted))'; this.style.borderColor='hsl(var(--border))'\"" : ''}>
                      ${zip}
                      ${
                        canRemove
                          ? `<button 
                        onclick="window.removeZipFromZone('${zoneId}', '${zip}')"
                        style="
                          position: absolute;
                          top: -6px;
                          right: -6px;
                          width: 18px;
                          height: 18px;
                          border-radius: 50%;
                          background: hsl(var(--destructive));
                          color: hsl(var(--destructive-foreground));
                          border: 2px solid hsl(var(--background));
                          font-size: 11px;
                          line-height: 1;
                          cursor: pointer;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                          transition: all 0.15s ease;
                          z-index: 10;
                        "
                        onmouseover="this.style.transform='scale(1.1)'"
                        onmouseout="this.style.transform='scale(1)'"
                        title="Remove ${zip}"
                      >×</button>`
                          : ''
                      }
                    </span>`;
                    })
                    .join('')}
                </div>
              </div>`
                : '';

            // Modern popup design with application theme colors
            const popupContent = `
            <div style="
              font-family: system-ui, -apple-system, 'Segoe UI', 'Roboto', sans-serif;
              background: hsl(var(--background));
              border-radius: 12px;
              padding: 0;
              margin: -4px;
              min-width: 280px;
              box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
              border: 1px solid hsl(var(--border));
            ">
              <!-- Header -->
              <div style="
                background: hsl(var(--primary));
                padding: 16px;
                border-radius: 12px 12px 0 0;
                color: hsl(var(--primary-foreground));
                position: relative;
                overflow: hidden;
              ">
                <div style="
                  position: absolute;
                  top: -50%;
                  right: -20px;
                  width: 100px;
                  height: 100px;
                  background: rgba(255, 255, 255, 0.1);
                  border-radius: 50%;
                "></div>
                <div style="
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  position: relative;
                ">
                  <div>
                    <h3 style="
                      margin: 0;
                      font-size: 16px;
                      font-weight: 600;
                      line-height: 1.3;
                      color: hsl(var(--primary-foreground));
                    ">${props!['zoneName']}</h3>
                    <p style="
                      margin: 4px 0 0 0;
                      font-size: 12px;
                      opacity: 0.9;
                      color: hsl(var(--primary-foreground) / 0.8);
                    ">${fullSizeLabel} Zone</p>
                  </div>
                  <div style="
                    background: ${sizeLabelColor};
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 14px;
                    color: ${showOversizedAdvisory ? 'hsl(var(--destructive-foreground))' : 'hsl(var(--primary-foreground))'};
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
                  ">${sizeLabel}</div>
                </div>
              </div>

              <!-- Content -->
              <div style="padding: 16px;">
                ${
                  showOversizedAdvisory
                    ? `
                  <div style="
                    background: hsl(var(--destructive) / 0.1);
                    border: 1px solid hsl(var(--destructive) / 0.2);
                    border-radius: 8px;
                    padding: 12px;
                    margin-bottom: 12px;
                  ">
                    <div style="
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      margin-bottom: 4px;
                    ">
                      <span style="font-size: 16px;">⚠️</span>
                      <span style="
                        font-weight: 600;
                        color: hsl(var(--destructive));
                        font-size: 12px;
                      ">Zone Too Large</span>
                    </div>
                    <p style="
                      margin: 0;
                      font-size: 11px;
                      color: hsl(var(--destructive) / 0.8);
                      line-height: 1.4;
                    ">Consider breaking into smaller zones for better management.</p>
                  </div>
                `
                    : ''
                }

                <!-- Stats Grid -->
                <div style="
                  display: grid;
                  grid-template-columns: 1fr 1fr;
                  gap: 12px;
                  margin-bottom: ${zipCodesDisplay ? '0' : '8px'};
                ">
                  <div style="
                    background: hsl(var(--muted) / 0.3);
                    border: 1px solid hsl(var(--border));
                    border-radius: 8px;
                    padding: 10px;
                    text-align: center;
                  ">
                    <div style="
                      font-size: 18px;
                      font-weight: 700;
                      color: ${showOversizedAdvisory ? 'hsl(var(--destructive))' : 'hsl(var(--primary))'};
                      margin-bottom: 2px;
                    ">${recallCount.toLocaleString()}</div>
                    <div style="
                      font-size: 10px;
                      color: hsl(var(--muted-foreground));
                      text-transform: uppercase;
                      font-weight: 600;
                      letter-spacing: 0.05em;
                    ">Recalls</div>
                  </div>
                  <div style="
                    background: hsl(var(--muted) / 0.3);
                    border: 1px solid hsl(var(--border));
                    border-radius: 8px;
                    padding: 10px;
                    text-align: center;
                  ">
                    <div style="
                      font-size: 18px;
                      font-weight: 700;
                      color: hsl(var(--primary));
                      margin-bottom: 2px;
                    ">${props!['zipCodeCount'] || 0}</div>
                    <div style="
                      font-size: 10px;
                      color: hsl(var(--muted-foreground));
                      text-transform: uppercase;
                      font-weight: 600;
                      letter-spacing: 0.05em;
                    ">ZIP Codes</div>
                  </div>
                </div>

                ${crawlWindow}

                ${zipCodesDisplay}
              </div>
            </div>
          `;

            layer.bindPopup(popupContent, {
              maxWidth: 320,
              className: 'modern-zone-popup'
            });
          }
        } as any);

        // Add to editable feature group so it can be deleted with the map delete tool
        editableZonesRef.current.addLayer(geoJsonLayer);
        zoneLayersRef.current.set(id, geoJsonLayer);

        // Add warning marker for extra large zones — only in Design mode.
        // Presentation mode is a customer-shareable surface, so the
        // "break into smaller zones" admin advisory has no place there.
        const isExtraLarge = isZoneTooLarge(
          feature.properties!['recallCount'],
          feature.properties!['oem']
        );
        const presentationActive = feature.properties!['presentationActive'];
        if (isExtraLarge && !presentationActive) {
          // Calculate center of the zone for warning marker placement
          const bounds = geoJsonLayer.getBounds();
          const center = bounds.getCenter();

          const warningMarker = L.marker([center.lat, center.lng], {
            icon: L.divIcon({
              html: `<div style="
              background: linear-gradient(45deg, #dc2626, #ef4444);
              color: white;
              border-radius: 50%;
              width: 30px;
              height: 30px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: bold;
              font-size: 16px;
              border: 2px solid white;
              box-shadow: 0 2px 8px rgba(220, 38, 38, 0.4);
              animation: pulse 2s infinite;
            ">⚠️</div>
            <style>
              @keyframes pulse {
                0% { transform: scale(1); box-shadow: 0 2px 8px rgba(220, 38, 38, 0.4); }
                50% { transform: scale(1.1); box-shadow: 0 4px 12px rgba(220, 38, 38, 0.6); }
                100% { transform: scale(1); box-shadow: 0 2px 8px rgba(220, 38, 38, 0.4); }
              }
            </style>`,
              className: 'warning-marker',
              iconSize: L.point(30, 30)
            })
          });

          warningMarker.bindPopup(`
          <div style="padding: 8px; text-align: center;">
            <div style="font-weight: 600; color: #dc2626; margin-bottom: 4px;">⚠️ Zone Too Large</div>
            <div style="font-size: 12px; color: #666; margin-bottom: 4px;">
              ${feature.properties!['zoneName']}<br>
              ${feature.properties!['recallCount']?.toLocaleString()} recalls
            </div>
            <div style="font-size: 11px; color: #dc2626; font-style: italic;">
              Break into smaller zones
            </div>
          </div>
        `);

          editableZonesRef.current.addLayer(warningMarker);
        }
      });

      return () => {
        editableZonesRef.current.clearLayers();
        zoneLayersRef.current.clear();
      };
    }, [map, zoneGeoJsonData]);

    // Render Presentation-mode ZIP subdivisions. These sit *under* the
    // OEM-color zone polygon (rendered by the main zone effect above) and
    // give the customer a quiet, low-contrast view of the ZIP-level shape
    // inside their territory. Zone-to-zone separation is intentionally
    // handled by the zone polygon's own heavy stroke, not by these layers.
    useEffect(() => {
      // Always wipe prior layers. Cleaning up here (not just in the cleanup
      // function) means switching modes or losing presentationMode mid-flight
      // doesn't leave stale subdivision lines behind.
      zoneZipSubdivisionLayersRef.current.forEach(layer => map.removeLayer(layer));
      zoneZipSubdivisionLayersRef.current = [];

      if (!presentationMode) {
        return;
      }

      const zipBoundariesByZone = presentationMode.zipBoundariesByZone;

      zones
        .filter(zone => zone.metadata && zone.metadata['geometry'])
        .forEach(zone => {
          // ZIP-subdivision layer: hairline-weight, near-tile-color strokes
          // with a dashed pattern. The dash pattern is the key UX signal —
          // a dashed line *cannot* be confused with the solid Vehix-orange
          // zone polygon stroke, so the eye instantly sorts the two into
          // categories: solid orange = territory boundary, dashed gray =
          // internal ZIP texture. Low opacity makes the dashes feel like
          // shadow rather than ink.
          const zoneBoundaries = zipBoundariesByZone?.get(zone.id);
          if (zoneBoundaries && zoneBoundaries.features.length > 0) {
            try {
              const zipsLayer = L.geoJSON(zoneBoundaries, {
                style: () =>
                  ({
                    color: '#94a3b8',
                    weight: 0.6,
                    opacity: 0.45,
                    dashArray: '2 3',
                    fillOpacity: 0,
                    lineCap: 'butt',
                    lineJoin: 'round',
                    interactive: false
                  }) as L.PathOptions
              });
              zipsLayer.addTo(map);
              zoneZipSubdivisionLayersRef.current.push(zipsLayer);
            } catch {
              // Defensive: a single zone's boundaries failing shouldn't break
              // the rest of the map.
            }
          }
        });

      // Push ZIP-subdivision layers below the OEM-color zone polygons so the
      // heavy zone strokes always render on top of the subdivision lines.
      zoneZipSubdivisionLayersRef.current.forEach(layer => layer.bringToBack());

      return () => {
        zoneZipSubdivisionLayersRef.current.forEach(layer => map.removeLayer(layer));
        zoneZipSubdivisionLayersRef.current = [];
      };
    }, [map, zones, presentationMode]);

    // Render centroid cards as Leaflet Markers with DivIcons when the page is
    // in Presentation mode. Each card sits at the polygon's pointOnFeature
    // (guaranteed to land inside the shape, even for concave/coastline zones)
    // and shows zone name + size + ZIP count + (toggleable) recall counts.
    useEffect(() => {
      // Always clear prior cards on every change so toggling Presentation off
      // (or flipping recall-line toggles) doesn't leave stale markers behind.
      centroidMarkersRef.current.forEach(marker => map.removeLayer(marker));
      centroidMarkersRef.current = [];

      if (!presentationMode || !presentationMode.showCentroidCards) {
        return;
      }

      const selectedSet = new Set(presentationMode.selectedZoneIds);
      const showAll = selectedSet.size === 0;

      zones
        .filter(zone => zone.metadata && zone.metadata['geometry'])
        .filter(zone => showAll || selectedSet.has(zone.id))
        .forEach(zone => {
          const geometry = zone.metadata!['geometry'] as GeoJSON.Geometry;
          let lat: number;
          let lng: number;
          try {
            const point = pointOnFeature({
              type: 'Feature',
              geometry,
              properties: {}
            } as GeoJSON.Feature<GeoJSON.Geometry>);
            // pointOnFeature returns a Point Feature with [lng, lat] coords.
            const coords = (point.geometry as GeoJSON.Point).coordinates;
            lng = coords[0]!;
            lat = coords[1]!;
          } catch {
            // Defensive: skip zones with malformed geometry rather than crash
            // the whole render.
            return;
          }

          const recallsAtCreation = zone.total_recalls_at_creation ?? 0;
          const recallsNow = presentationMode.recallCountsNow?.[zone.id];
          // Size describes the zone now. Prefer the live presentation count, else the
          // server-refreshed column; the creation baseline is shown separately.
          // 'customer' — this effect early-returns unless Presentation mode is active.
          const sizeLabel = getZoneSizeLabel(
            recallsNow ?? zone.total_recalls_current ?? 0,
            zone.oem,
            'customer'
          );
          const zipCount = zone.zip_code_count ?? zone.zip_codes?.length ?? 0;
          // Centroid card accent matches the zone's assigned fill so the
          // visual chain is unbroken: card edge → polygon fill → boundary.
          // Fall back to the brand-orange palette entry if the assignment
          // hasn't computed yet (first-paint race).
          const paletteIndex = presentationMode.zoneColorAssignment?.get(zone.id) ?? 1;
          const cardPalette =
            VEHIX_ZONE_PALETTE[paletteIndex % VEHIX_ZONE_PALETTE.length] ?? VEHIX_ZONE_PALETTE[1]!;

          const nowLine =
            presentationMode.showNowRecalls && recallsNow !== undefined
              ? `<div style="font-size:11px;color:#0f172a;margin-top:2px;"><span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:${cardPalette.fill};margin-right:6px;vertical-align:middle;"></span>Now: <strong>${recallsNow.toLocaleString()}</strong> recalls</div>`
              : '';
          const creationLine = presentationMode.showCreationRecalls
            ? `<div style="font-size:11px;color:#475569;margin-top:1px;"><span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:${cardPalette.stroke};margin-right:6px;vertical-align:middle;opacity:0.7;"></span>Creation: <strong>${recallsAtCreation.toLocaleString()}</strong> recalls</div>`
            : '';

          const html = `
            <div style="
              background: rgba(255,255,255,0.97);
              border: 1.5px solid ${cardPalette.stroke};
              border-left: 4px solid ${cardPalette.fill};
              border-radius: 8px;
              padding: 8px 10px;
              box-shadow: 0 4px 12px rgba(15,23,42,0.18);
              font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
              min-width: 160px;
              max-width: 220px;
              pointer-events: none;
            ">
              <div style="font-size:13px;font-weight:600;color:#0f172a;line-height:1.2;">${zone.zone_name}</div>
              <div style="font-size:11px;color:#64748b;margin-top:3px;">${sizeLabel} · ${zipCount} ZIP${zipCount === 1 ? '' : 's'}</div>
              ${nowLine}
              ${creationLine}
            </div>
          `;

          const icon = L.divIcon({
            html,
            className: 'zone-centroid-card',
            iconSize: undefined as unknown as L.PointExpression, // Let CSS size the card naturally
            iconAnchor: [110, 30] // Approximate horizontal center, top-aligned offset
          });

          const marker = L.marker([lat, lng], {
            icon,
            interactive: false, // Cards are pure presentation, never block map clicks
            keyboard: false
          });
          marker.addTo(map);
          centroidMarkersRef.current.push(marker);
        });

      return () => {
        centroidMarkersRef.current.forEach(marker => map.removeLayer(marker));
        centroidMarkersRef.current = [];
      };
    }, [map, zones, presentationMode]);

    // Set up global function for ZIP code removal
    useEffect(() => {
      if (onRemoveZipCode) {
        (window as any).removeZipFromZone = (zoneId: string, zipCode: string) => {
          const zone = zones.find(z => z.id === zoneId);
          if (zone) {
            onRemoveZipCode(zone, zipCode);
          }
        };
      }

      return () => {
        delete (window as any).removeZipFromZone;
      };
    }, [zones, onRemoveZipCode]);

    // Optimized bounds change handler
    const handleMapMove = useCallback(() => {
      if (!onMapBoundsChange) return;

      if (boundsTimerRef.current) {
        clearTimeout(boundsTimerRef.current);
      }

      boundsTimerRef.current = setTimeout(() => {
        try {
          const bounds = map.getBounds();
          onMapBoundsChange({
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest()
          });
        } catch (error) {
          // Silently handle errors
        }
      }, 500); // Increased debounce for better performance
    }, [map, onMapBoundsChange]);

    // Handle map bounds changes for viewport loading
    useEffect(() => {
      if (!onMapBoundsChange) return;

      // Set initial bounds once after map is ready (use a small delay to ensure map is fully initialized)
      const initialTimer = setTimeout(() => {
        handleMapMove();
      }, 100);

      map.on('moveend', handleMapMove);
      map.on('zoomend', handleMapMove);

      return () => {
        clearTimeout(initialTimer);
        if (boundsTimerRef.current) {
          clearTimeout(boundsTimerRef.current);
        }
        map.off('moveend', handleMapMove);
        map.off('zoomend', handleMapMove);
      };
    }, [map, handleMapMove]);

    return null;
  }
);

MapController.displayName = 'MapController';

export const ZoneDrawingMapOptimized: React.FC<ZoneDrawingMapOptimizedProps> = React.memo(
  ({
    dealerLocations,
    zones,
    onShapeDrawn,
    onMapBoundsChange,
    selectedZoneId,
    onZoneDelete,
    onRemoveZipCode,
    loading = false,
    clearDrawnShapes,
    showBubbles = true,
    uniformColor = false,
    drawingEnabled = true,
    recallCountsNow,
    crawlDatesNow,
    presentationMode
  }) => {
    // Center map on Florida (same as recall map)
    const center: [number, number] = [27.9944024, -81.7602544];
    const zoom = 7;

    return (
      // The id is the stable hook for html2canvas-based PNG export. Don't
      // rename without updating useMapPresentationExport.
      <div
        id='zone-map-leaflet-root'
        className='relative w-full h-full'
        style={{ overflow: 'hidden' }}
      >
        {loading && (
          <div className='absolute top-4 right-4 z-[1000] bg-white rounded-lg shadow-md p-2'>
            <div className='flex items-center gap-2'>
              <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-primary' />
              <span className='text-sm text-muted-foreground'>Loading dealers...</span>
            </div>
          </div>
        )}
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ height: '100%', width: '100%', position: 'relative' }}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
            crossOrigin='anonymous'
          />
          <MapController
            onShapeDrawn={onShapeDrawn}
            dealerLocations={dealerLocations}
            zones={zones}
            showBubbles={showBubbles}
            uniformColor={uniformColor}
            drawingEnabled={drawingEnabled}
            {...(recallCountsNow && { recallCountsNow })}
            {...(crawlDatesNow && { crawlDatesNow })}
            {...(presentationMode && { presentationMode })}
            {...(selectedZoneId && { selectedZoneId })}
            {...(onMapBoundsChange && { onMapBoundsChange })}
            {...(onZoneDelete && { onZoneDelete })}
            {...(onRemoveZipCode && { onRemoveZipCode })}
            {...(clearDrawnShapes && { clearDrawnShapes })}
          />
          {presentationMode && (
            <ZoneLabelOverlay
              zones={zones}
              {...(presentationMode.zoneColorAssignment && {
                zoneColorAssignment: presentationMode.zoneColorAssignment
              })}
              {...(presentationMode.recallCountsNow && {
                recallCountsNow: presentationMode.recallCountsNow
              })}
              {...(presentationMode.tenantByZone && {
                tenantByZone: presentationMode.tenantByZone
              })}
              showNowRecalls={presentationMode.showNowRecalls}
              showCreationRecalls={presentationMode.showCreationRecalls}
              showTenantNames={presentationMode.showTenantNames}
              selectedZoneIds={presentationMode.selectedZoneIds}
            />
          )}
        </MapContainer>
        {/* Availability key. Skipped by the PNG export, whose legend strip
            carries the same key below the map. */}
        {presentationMode && (
          <div
            data-export-ignore='true'
            className={cn(
              'absolute bottom-3 left-3 z-[500] pointer-events-none flex items-center gap-3',
              'rounded-md border bg-card/95 px-2.5 py-1.5 text-[11px] shadow-sm backdrop-blur-sm'
            )}
          >
            <span className='flex items-center gap-1.5'>
              <span
                className='inline-block h-3 w-3 rounded-sm'
                style={{
                  background: AVAILABLE_ZONE_COLOR.fill,
                  border: '1px solid #475569'
                }}
              />
              Available
            </span>
            <span className='flex items-center gap-1.5 text-muted-foreground'>
              <span
                className='inline-block h-3 w-3 rounded-sm'
                style={{
                  background: `${ASSIGNED_ZONE_COLOR.fill}66`,
                  border: `1px dashed ${ASSIGNED_ZONE_COLOR.stroke}`
                }}
              />
              Assigned
            </span>
          </div>
        )}
      </div>
    );
  }
);

ZoneDrawingMapOptimized.displayName = 'ZoneDrawingMapOptimized';
